import {
  Add as AddIcon,
  ChevronLeft as ChevronLeftIcon,
  Description as DescriptionIcon,
  FactCheck as FactCheckIcon,
  FolderShared as FolderSharedIcon,
  Gavel as GavelIcon,
  HelpOutline as HelpOutlineIcon,
  Home as HomeIcon,
  LibraryBooks as LibraryBooksIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Movie as MovieIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGroups } from '../contexts/GroupContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { useThemeColors } from '../hooks/useThemeColors';
import { createGroup } from '../services/groupService';
import GroupFormDialog, { GROUP_COLORS } from './GroupFormDialog';
import NotificationBell from './NotificationBell';

/** サイドバーの幅。開いているときは項目名と説明を出し、畳むとアイコンだけになる */
const DRAWER_OPEN = 240;
const DRAWER_CLOSED = 76;
const SIDEBAR_KEY = 'mikiwame-sidebar-collapsed';

interface LayoutProps {
  children: React.ReactNode;
}

// カラーパレット（Settings.tsxと同じ）
const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, signOut } = useAuth();
  const { groups, refreshGroups } = useGroups();
  const { themeMode } = useThemeMode();
  const tokens = useThemeColors();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // サイドバーの開閉。既定は開いた状態（初めての人が項目名を読めるように）。
  // 選んだ状態は次回も引き継ぐ。狭い画面では本文の幅を優先して自動で畳む。
  const narrow = useMediaQuery('(max-width:1200px)');
  const [collapsedPref, setCollapsedPref] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) === '1';
    } catch {
      return false;
    }
  });
  const collapsed = narrow || collapsedPref;
  const drawerWidth = collapsed ? DRAWER_CLOSED : DRAWER_OPEN;
  const toggleSidebar = () => {
    setCollapsedPref((v) => {
      const next = !v;
      try {
        localStorage.setItem(SIDEBAR_KEY, next ? '1' : '0');
      } catch {
        /* 保存できなくても動作には影響しない */
      }
      return next;
    });
  };

  // ユーザーメニュー
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [createMenuAnchor, setCreateMenuAnchor] = useState<null | HTMLElement>(null);
  const userMenuOpen = Boolean(userMenuAnchor);

  // グループ作成ダイアログ
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [selectedColor, setSelectedColor] = useState(GROUP_COLORS[0]);
  const [creating, setCreating] = useState(false);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  /** 「新しく検証する」の行き先。3つの入口が別画面なので、押したら選んでもらう。
   *  いまはホーム画面の中にしか入口が無く、他の画面からは一度戻る必要があった。 */
  const createItems = [
    {
      text: '文書のファクトチェック',
      icon: <DescriptionIcon sx={{ fontSize: 20 }} />,
      path: '/create',
    },
    {
      text: '動画のファクトチェック',
      icon: <MovieIcon sx={{ fontSize: 20 }} />,
      path: '/create-video',
    },
    {
      text: '特定のサイトに限定してファクトチェック',
      icon: <FactCheckIcon sx={{ fontSize: 20 }} />,
      path: '/domain-factcheck',
    },
  ];

  const menuItems = [
    {
      text: 'ホーム',
      desc: 'できることを選ぶ',
      icon: <HomeIcon sx={{ fontSize: 22 }} />,
      path: '/',
    },
    {
      // トップを窓口だけにしたので、検証したものはここから開く（#150）
      text: 'マイアイテム',
      desc: '検証した資料と追加リサーチ',
      icon: <LibraryBooksIcon sx={{ fontSize: 22 }} />,
      path: '/items',
    },
    {
      text: '調査',
      desc: '気になる主張を深掘り',
      icon: <SearchIcon sx={{ fontSize: 22 }} />,
      path: '/investigation',
    },
    {
      text: 'サイトを限定',
      desc: '決めたサイトだけで調べる',
      icon: <FactCheckIcon sx={{ fontSize: 22 }} />,
      path: '/domain-factcheck',
    },
    {
      text: 'グループ共有',
      desc: 'メンバーと資料を共有',
      // 予定表のアイコンが当たっていて中身と合っていなかったので、資料＋人の絵にする
      icon: <FolderSharedIcon sx={{ fontSize: 22 }} />,
      path: '/share',
    },
    {
      text: '規程チェック',
      desc: '放送基準に照らして点検',
      icon: <GavelIcon sx={{ fontSize: 22 }} />,
      path: '/compliance',
    },
  ];

  const handleGroupClick = (groupId: string) => {
    navigate(`/share?group=${groupId}`);
  };

  const handleCreateGroup = async () => {
    // ... existing implementation ...
    if (!user || !newGroupName.trim()) return;

    try {
      setCreating(true);
      await createGroup(
        user.uid,
        {
          name: newGroupName.trim(),
          description: newGroupDescription.trim() || undefined,
          color: selectedColor,
        },
        {
          displayName: user.displayName || 'Unknown User',
          photoURL: user.photoURL || undefined,
        },
      );

      setCreateDialogOpen(false);
      setNewGroupName('');
      setNewGroupDescription('');
      setSelectedColor(GROUP_COLORS[0]);
      await refreshGroups();
    } catch (err) {
      console.error('Error creating group:', err);
    } finally {
      setCreating(false);
    }
  };

  const getGroupInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  /** 1項目。畳んでいるときはアイコンだけにして、名前はツールチップで出す */
  const navRow = (opts: {
    key: string;
    icon: React.ReactNode;
    label: string;
    desc?: string;
    selected?: boolean;
    onClick: () => void;
    dense?: boolean;
  }) => {
    const { key, icon, label, desc, selected, onClick, dense } = opts;
    const row = (
      <ListItemButton
        key={key}
        onClick={onClick}
        sx={{
          borderRadius: '10px',
          minHeight: dense ? 40 : 44,
          px: 1.25,
          py: dense ? 0.75 : 1,
          gap: 1.4,
          justifyContent: collapsed ? 'center' : 'flex-start',
          color: selected
            ? '#00d2ff'
            : themeMode === 'dark'
              ? 'rgba(255,255,255,.55)'
              : 'rgba(0,0,0,.62)',
          backgroundColor: selected ? 'rgba(0,210,255,.12)' : 'transparent',
          border: `1px solid ${selected ? 'rgba(0,210,255,.3)' : 'transparent'}`,
          transition: 'background-color .18s, color .18s',
          '&:hover': {
            backgroundColor: 'rgba(0,210,255,.08)',
            color: themeMode === 'dark' ? '#fff' : '#1a1a2e',
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 'auto', color: 'inherit' }}>{icon}</ListItemIcon>
        {!collapsed && (
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: selected ? 800 : 700,
                lineHeight: 1.35,
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </Typography>
            {desc && (
              <Typography
                sx={{
                  fontSize: 10.5,
                  lineHeight: 1.35,
                  color: themeMode === 'dark' ? 'rgba(255,255,255,.38)' : 'rgba(0,0,0,.45)',
                  whiteSpace: 'nowrap',
                }}
              >
                {desc}
              </Typography>
            )}
          </Box>
        )}
      </ListItemButton>
    );
    return collapsed ? (
      <Tooltip key={key} title={label} placement="right">
        <Box>{row}</Box>
      </Tooltip>
    ) : (
      row
    );
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', px: 1.25, py: 1.75 }}>
      {/* ブランドと開閉 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 0.75,
          pb: 1.75,
          minHeight: 40,
        }}
      >
        <Box
          component="img"
          src="/favicon.svg"
          alt=""
          onClick={() => navigate('/')}
          sx={{
            width: 28,
            height: 28,
            flexShrink: 0,
            cursor: 'pointer',
            filter: 'drop-shadow(0 0 8px rgba(100,255,218,.7))',
          }}
        />
        {/* ヘッダー中央にも同じロゴが出るので、ここに「MIKIWAME」の文字は置かない */}
        {!collapsed && <Box sx={{ flex: 1 }} />}
        {!narrow && (
          <Tooltip title={collapsed ? 'メニューを開く' : 'メニューを畳む'} placement="right">
            <IconButton
              onClick={toggleSidebar}
              size="small"
              // 既定の波紋（円形に広がるアニメーション）が押すたびに丸い輪として残って見えるので止める。
              // キーボード操作のときだけ分かるように、focus-visible でのみ枠を出す。
              disableRipple
              disableFocusRipple
              sx={{
                ml: collapsed ? 0 : 'auto',
                color: tokens.muted,
                borderRadius: '8px',
                '&:hover': { color: '#00d2ff', backgroundColor: 'rgba(0,210,255,.1)' },
                '&:focus': { outline: 'none' },
                '&.Mui-focusVisible': {
                  outline: '2px solid rgba(0,210,255,.6)',
                  outlineOffset: '1px',
                },
              }}
            >
              <ChevronLeftIcon
                sx={{
                  fontSize: 20,
                  transform: collapsed ? 'rotate(180deg)' : 'none',
                  transition: 'transform .2s',
                }}
              />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* 一番よく使う操作。入口が3つに分かれているので押したら選んでもらう */}
      <Tooltip title={collapsed ? '新しく検証する' : ''} placement="right">
        <Button
          onClick={(e) => setCreateMenuAnchor(e.currentTarget)}
          startIcon={collapsed ? undefined : <AddIcon sx={{ fontSize: 18 }} />}
          sx={{
            mb: 1.75,
            minWidth: 0,
            px: collapsed ? 0 : 1.5,
            py: 1.1,
            borderRadius: 2.5,
            fontSize: 12.5,
            fontWeight: 800,
            color: '#fff',
            background: 'linear-gradient(120deg, #667eea, #764ba2)',
            boxShadow: '0 5px 14px rgba(102,126,234,.3)',
            whiteSpace: 'nowrap',
            '&:hover': {
              background: 'linear-gradient(120deg, #5a6fd8, #6a4292)',
              boxShadow: '0 7px 18px rgba(102,126,234,.42)',
            },
          }}
        >
          {collapsed ? <AddIcon sx={{ fontSize: 20 }} /> : '新しく検証する'}
        </Button>
      </Tooltip>
      <Menu
        anchorEl={createMenuAnchor}
        open={Boolean(createMenuAnchor)}
        onClose={() => setCreateMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, minWidth: 248, borderRadius: 2 } } }}
      >
        {createItems.map((item) => (
          <MenuItem
            key={item.path}
            onClick={() => {
              setCreateMenuAnchor(null);
              setMobileOpen(false);
              navigate(item.path);
            }}
            sx={{ gap: 1.25, py: 1.1, fontSize: 13, fontWeight: 600 }}
          >
            <ListItemIcon sx={{ minWidth: 0, color: tokens.muted }}>{item.icon}</ListItemIcon>
            {item.text}
          </MenuItem>
        ))}
      </Menu>

      {/* メニュー */}
      <List sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 0 }}>
        {menuItems.map((item) =>
          navRow({
            key: item.text,
            icon: item.icon,
            label: item.text,
            desc: item.desc,
            selected:
              location.pathname === item.path ||
              (item.path === '/share' && location.pathname.startsWith('/share')) ||
              // アイテム詳細（/items/:itemId）でも「マイアイテム」を選択中に見せる
              (item.path === '/items' && location.pathname.startsWith('/items')),
            onClick: () => navigate(item.path),
          }),
        )}
      </List>

      <Box sx={{ flexGrow: 1 }} />

      {/* グループ */}
      {!collapsed && (
        <Typography
          sx={{
            fontSize: 9.5,
            fontWeight: 800,
            letterSpacing: '.1em',
            color: tokens.faint,
            px: 1.25,
            pb: 0.5,
          }}
        >
          GROUPS
        </Typography>
      )}
      <Box sx={{ maxHeight: 220, overflowY: 'auto', overflowX: 'hidden' }}>
        <List sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, p: 0 }}>
          {groups.map((group) =>
            navRow({
              key: group.id,
              dense: true,
              icon: (
                <Avatar
                  sx={{
                    width: 22,
                    height: 22,
                    bgcolor: group.color,
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    color: '#fff',
                  }}
                >
                  {getGroupInitials(group.name)}
                </Avatar>
              ),
              label: group.name,
              onClick: () => handleGroupClick(group.id),
            }),
          )}
          {navRow({
            key: '__add_group',
            dense: true,
            icon: (
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  border: `1px dashed ${tokens.line}`,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <AddIcon sx={{ fontSize: 14 }} />
              </Box>
            ),
            label: 'グループを作る',
            onClick: () => setCreateDialogOpen(true),
          })}
        </List>
      </Box>

      {/* 設定 */}
      <Box sx={{ mt: 0.75, pt: 0.75, borderTop: `1px solid ${tokens.line}` }}>
        <List sx={{ p: 0 }}>
          {navRow({
            key: '__settings',
            dense: true,
            icon: <SettingsIcon sx={{ fontSize: 22 }} />,
            label: '設定',
            selected: location.pathname === '/settings',
            onClick: () => navigate('/settings'),
          })}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: 'var(--app-vh)',
        bgcolor: themeMode === 'dark' ? '#050b14' : '#f0f2f5',
      }}
    >
      {/* Background Ambience */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          backgroundImage:
            themeMode === 'dark'
              ? 'url(/images/background.jpeg)'
              : 'url(/images/white_background.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          pointerEvents: 'none',
        }}
      >
        {/* Overlay to ensure text readability */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor:
              themeMode === 'dark' ? 'rgba(5, 11, 20, 0.15)' : 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'none',
          }}
        />
      </Box>

      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          background:
            themeMode === 'dark'
              ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.5) 0%, rgba(118, 75, 162, 0.45) 100%)'
              : 'linear-gradient(135deg, rgba(102, 126, 234, 0.85) 0%, rgba(139, 92, 246, 0.75) 100%)',
          backdropFilter: 'blur(16px)',
          boxShadow:
            themeMode === 'dark'
              ? '0 4px 24px rgba(102, 126, 234, 0.25)'
              : '0 4px 24px rgba(102, 126, 234, 0.15)',
          borderBottom:
            themeMode === 'dark'
              ? '1px solid rgba(138, 100, 255, 0.25)'
              : '1px solid rgba(102, 126, 234, 0.2)',
          zIndex: 1100,
          pt: 1,
        }}
      >
        <Toolbar sx={{ position: 'relative' }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          {/* ロゴは紫エリアの真の中央に固定（右の通知/アバターに影響されない） */}
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              py: 0.5,
            }}
          >
            <Box
              component="img"
              src="/mikiwame-logo.svg"
              alt="MIKIWAME"
              sx={{
                height: 60,
                filter:
                  themeMode === 'dark'
                    ? 'drop-shadow(0 0 15px rgba(0, 176, 255, 0.4))'
                    : 'drop-shadow(0.2px 0 0 rgba(0, 0, 0, 0.4)) drop-shadow(-0.2px 0 0 rgba(0, 0, 0, 0.4)) drop-shadow(0 0.2px 0 rgba(0, 0, 0, 0.4)) drop-shadow(0 -0.2px 0 rgba(0, 0, 0, 0.4))',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/')}
            />
          </Box>

          {/* User Profile & Notifs */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', ml: 'auto' }}>
            <NotificationBell />
            {user && (
              <>
                <Tooltip title="アカウントメニュー">
                  <IconButton onClick={handleUserMenuOpen} sx={{ p: 0 }}>
                    <Avatar
                      src={user.photoURL || undefined}
                      sx={{
                        width: 36,
                        height: 36,
                        border: '2px solid rgba(100, 255, 218, 0.5)',
                        bgcolor: 'rgba(100, 255, 218, 0.1)',
                        color: '#64ffda',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        '&:hover': {
                          boxShadow: '0 0 15px rgba(100, 255, 218, 0.5)',
                          borderColor: '#64ffda',
                        },
                      }}
                    >
                      {user.displayName ? user.displayName[0].toUpperCase() : 'U'}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={userMenuAnchor}
                  open={userMenuOpen}
                  onClose={handleUserMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      minWidth: 200,
                      bgcolor: themeMode === 'dark' ? '#1a2332' : 'rgba(255, 255, 255, 0.95)',
                      border:
                        themeMode === 'dark'
                          ? '1px solid rgba(255,255,255,0.1)'
                          : '1px solid rgba(0,0,0,0.1)',
                      backdropFilter: 'blur(10px)',
                    },
                  }}
                >
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, color: themeMode === 'dark' ? '#fff' : '#1a1a2e' }}
                    >
                      {user.displayName || 'ユーザー'}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: themeMode === 'dark' ? 'text.secondary' : 'rgba(0,0,0,0.6)' }}
                    >
                      {user.email}
                    </Typography>
                  </Box>
                  <Divider
                    sx={{
                      borderColor:
                        themeMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                    }}
                  />
                  <MenuItem
                    onClick={() => {
                      handleUserMenuClose();
                      navigate('/settings');
                    }}
                    sx={{ py: 1.5, color: themeMode === 'dark' ? '#fff' : '#1a1a2e' }}
                  >
                    <ListItemIcon>
                      <PersonIcon
                        fontSize="small"
                        sx={{ color: themeMode === 'dark' ? '#fff' : '#1a1a2e' }}
                      />
                    </ListItemIcon>
                    設定
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleUserMenuClose();
                      navigate('/help');
                    }}
                    sx={{ py: 1.5, color: themeMode === 'dark' ? '#fff' : '#1a1a2e' }}
                  >
                    <ListItemIcon>
                      <HelpOutlineIcon
                        fontSize="small"
                        sx={{ color: themeMode === 'dark' ? '#fff' : '#1a1a2e' }}
                      />
                    </ListItemIcon>
                    ヘルプ
                  </MenuItem>
                  <Divider
                    sx={{
                      borderColor:
                        themeMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                    }}
                  />
                  <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: '#ef4444' }}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" sx={{ color: '#ef4444' }} />
                    </ListItemIcon>
                    ログアウト
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Navigation Drawer */}
      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 }, zIndex: 1200 }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor:
                themeMode === 'dark' ? 'rgba(10, 16, 29, 0.85)' : 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(10px)',
              borderRight:
                themeMode === 'dark'
                  ? '1px solid rgba(255,255,255,0.1)'
                  : '1px solid rgba(0,0,0,0.15)',
            },
          }}
        >
          {drawerContent}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              // 開閉をなめらかに見せる
              transition: 'width .22s cubic-bezier(.4,0,.2,1)',
              overflowX: 'hidden',
              bgcolor: 'transparent',
              backdropFilter: 'none',
              borderRight:
                themeMode === 'dark'
                  ? '1px solid rgba(255, 255, 255, 0.08)'
                  : '1px solid rgba(0, 0, 0, 0.15)',
              boxShadow: 'none',
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 4,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          zIndex: 1,
        }}
      >
        {children}
      </Box>

      {/* グループ作成ダイアログ。中身は設定画面と共通の部品 */}
      <GroupFormDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        mode="create"
        name={newGroupName}
        onNameChange={setNewGroupName}
        description={newGroupDescription}
        onDescriptionChange={setNewGroupDescription}
        color={selectedColor}
        onColorChange={setSelectedColor}
        onSubmit={handleCreateGroup}
        submitting={creating}
      />
    </Box>
  );
};

export default Layout;
